import React, { useRef, useState, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';

import {
    Sizes,
    MessagePropType,
    PageBehaviourButton,
    toDate,
    DefaultIntlFormatValues,
    messageToString,
    IconPlus,
    IconMinus,
    PrimaryButton,
    useSmoothCollapse,
    FilteredAsyncContainer,
} from '@ratehub/base-ui';

import COMMON_MESSAGES from '../definitions/CommonMessages';


function ProductList({
    title,
    lastUpdated,

    primaryFilters,
    additionalFilters = [],
    initialFilterValues,
    initialFetchedProducts,
    getFormValues,
    onFetchValue,

    maxRowsCollapsed = 6,
    maxRowsExpanded = Infinity,

    noMatchingItemsMessage,

    ctaMessage,
    ctaOnClick,

    children,

    className,
    ...otherProps
}) {
    const intl = useIntl();
    const [ isAlwaysExpanded, setIsAlwaysExpanded ] = useState(false);
    const [ filterValues, setFilterValues ] = useState(initialFilterValues);

    // allow 0 for maxRowsCollapsed, but if maxRowsCollapsed is < 0 set it to Infinity, making isAlwaysExpanded true
    // (but preferred way to indicate isAlwaysExpanded is with `maxRowsCollapsed={Infinity}`)
    if (maxRowsCollapsed < 0) {
        maxRowsCollapsed = Infinity;
    }

    const containerRef = useRef(null);
    const [ isExpanded, toggleIsExpanded ] = useSmoothCollapse({
        containerRef,
        controlledIsExpanded: isAlwaysExpanded,
    });

    const hasCTAButton = ctaMessage && ctaOnClick;

    // Callbacks to FilteredAsyncContainer must be memoized to prevent infinite re-renders
    const handleFetchValue = useCallback(async (values) => {
        const products = await onFetchValue(values);
        // we delay setting filterValues used as second argument to render prop children because otherwise
        // we may see product changes before we have completed the fetch
        setFilterValues(values);

        setIsAlwaysExpanded(products.length <= maxRowsCollapsed);
        // TODO: ideally, if they’ve changed products, we may want to reset isExpanded to false

        return products;
    }, []);

    return (
        <section
            className={className}
            {...otherProps}
        >
            <header className="product-list-header rh-display-flex rh-justify-content-space-between rh-flex-wrap">
                <div className="product-list-flex rh-display-flex rh-mb-1_5">
                    <If condition={title}>
                        <h2 className={classNames('rh-title-2xl', 'showDot', 'rh-mt-0', 'rh-mb-0_5')}>
                            {messageToString(title, intl)}
                        </h2>
                    </If>

                    <If condition={lastUpdated}>
                        <p className="rh-m-0 rh-text-s">
                            {intl.formatMessage(COMMON_MESSAGES.ratesUpdated, {
                                timestamp: toDate(lastUpdated),
                                ...DefaultIntlFormatValues,
                            })}
                        </p>
                    </If>
                </div>
            </header>

            <FilteredAsyncContainer
                primaryFilters={primaryFilters}
                additionalFilters={additionalFilters}

                fetchedValueName="products"
                initialFetchedValue={initialFetchedProducts}
                getFormValues={getFormValues}
                onFetchValue={handleFetchValue}

                debounce={400}
                hasBorderAroundChildren={false}
            >
                {({ products }) => (
                    <Choose>
                        <When condition={products?.length}>
                            <ol
                                ref={containerRef}
                                className="product-list rh-m-0 rh-pl-0"
                            >
                                <For
                                    each="product"
                                    of={products.slice(0, isExpanded ? maxRowsExpanded : maxRowsCollapsed)}
                                    index="index"
                                >
                                    <li
                                        className="product-list-item separate-from-next"
                                        key={index}
                                    >
                                        {/* `children` must be a render prop that expects `product` as an argument */}
                                        {children(product, filterValues)}
                                    </li>
                                </For>
                            </ol>
                        </When>

                        <Otherwise>
                            <div className="rh-text-l rh-mt-1_5 rh-text-align-center">
                                <Choose>
                                    <When condition={noMatchingItemsMessage}>
                                        { noMatchingItemsMessage }
                                    </When>
                                    <Otherwise>
                                        <FormattedMessage
                                            id="web-components.product-list.no-matches"
                                            defaultMessage="No accounts match the given search criteria."
                                        />
                                    </Otherwise>
                                </Choose>
                            </div>
                        </Otherwise>
                    </Choose>
                )}
            </FilteredAsyncContainer>

            <If condition={!isAlwaysExpanded}>
                <PageBehaviourButton
                    className="rh-mt-0_5 rh-mb-1 rh-mx-auto"
                    data-name="product-list-show-more"
                    iconComponent={isExpanded ? IconMinus : IconPlus}
                    iconLocation="left"
                    message={(
                        <FormattedMessage
                            id="web-components.product-list.show-more-or-less"
                            defaultMessage="{isExpanded, select, true {Show less} other {Show more}}"
                            values={{ isExpanded }}
                        />
                    )}
                    size="medium"
                    onClick={toggleIsExpanded}
                />
            </If>

            <If condition={hasCTAButton}>
                <div className="cta-wrapper separate-from-next rh-mt-1 rh-p-1">
                    <PrimaryButton
                        message={ctaMessage}
                        onClick={ctaOnClick}
                        size="small"
                        className="cta rh-display-block rh-mx-auto"
                        data-name="product-list-cta"
                    />
                </div>
            </If>
        </section>
    );
}

ProductList.propTypes = {
    title: MessagePropType,
    lastUpdated: PropTypes.string,

    primaryFilters: PropTypes.arrayOf(
        PropTypes.node,
    ).isRequired,
    additionalFilters: PropTypes.arrayOf(
        PropTypes.node,
    ),
    initialFilterValues: PropTypes.object.isRequired,
    initialFetchedProducts: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.object),
        PropTypes.object, // mobx observable array
    ]).isRequired,
    getFormValues: PropTypes.func.isRequired,
    onFetchValue: PropTypes.func.isRequired,

    maxRowsCollapsed: PropTypes.number,
    maxRowsExpanded: PropTypes.number,

    noMatchingItemsMessage: MessagePropType,

    ctaMessage: PropTypes.string,
    ctaOnClick: PropTypes.func,

    children: PropTypes.func.isRequired,

    className: PropTypes.string,
};


export default styled(observer(ProductList))`
    > .product-list-header {
        > .product-list-flex {
            flex-direction: column;

            &:not(.product-list-location) {
                margin-right: ${Sizes.SPACING.TWO};
                margin-bottom: ${Sizes.SPACING.ONE_AND_A_HALF};
            }

            &.product-list-location {
                margin-top: 4px; /* optically align the smaller text block */
                flex-basis: 285px;
            }
        }
    }

    .filter-question-compact {
        margin: 0;
    }

    .product-list {
        list-style-type: none;

        > .product-list-item.separate-from-next {
            margin-bottom: ${Sizes.SPACING.ONE};
        }
    }

    > .cta-wrapper {
        > .cta {
            width: fit-content;     /* required here to override button’s 100% */
            white-space: nowrap;
        }
    }
`;
